/**
 * useDownsampledData.js
 *
 * Custom hook for LTTB (Largest Triangle Three Buckets) downsampling of chart data.
 *
 * WHY THIS EXISTS:
 * - Charts with 50,000+ data points create 100,000+ SVG elements
 * - Browsers freeze trying to render this many elements
 * - LTTB reduces points while preserving visual shape (peaks, valleys, trends)
 * - Result: 50,000 -> 2,000 points, chart looks identical, renders in seconds
 *
 * HOW IT WORKS:
 * 1. Tracks chart container width using ResizeObserver
 * 2. Calculates target points: width * 2 (2 points per pixel is optimal)
 * 3. Applies LTTB algorithm if data exceeds target
 * 4. Returns downsampled data that renders identically to full data
 *
 * USAGE:
 * const { downsampledData, containerRef } = useDownsampledData(chartData);
 * // Attach containerRef to chart container div
 * // Use downsampledData instead of chartData for rendering
 */
import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { LTTB } from 'downsample';

/**
 * Configuration defaults for downsampling
 */
const DEFAULT_CONFIG = {
  maxPointsPerPixel: 2,     // 2 points per pixel provides good visual fidelity
  minPoints: 500,           // Never downsample below this (prevents over-simplification)
  maxPoints: 3000,          // Cap to prevent excessive rendering even on wide screens
  defaultWidth: 800,        // Fallback width before ResizeObserver triggers
};

/**
 * Apply LTTB downsampling to an array of data points.
 * Preserves visual shape while reducing point count.
 *
 * @param {Array} data - Array of data points with timestamp and close/value fields
 * @param {number} targetPoints - Target number of points after downsampling
 * @param {string} valueKey - Key to use for Y value (default: 'close')
 * @returns {Array} Downsampled data array
 */
export function downsampleData(data, targetPoints, valueKey = 'close') {
  if (!data || data.length === 0) return data;
  if (data.length <= targetPoints) return data;

  // LTTB requires [x, y] pairs - use timestamp as x, specified value as y
  const xyData = data.map(d => {
    const yValue = d[valueKey] ?? d.value ?? d.close ?? 0;
    return [d.timestamp, yValue];
  });

  // Apply LTTB algorithm
  const downsampled = LTTB(xyData, targetPoints);

  // Map back to original data format by finding closest matching points
  // LTTB returns [timestamp, value] pairs, we need the full data objects
  const result = [];
  let dataIndex = 0;

  for (const [timestamp] of downsampled) {
    // Find the data point with the closest timestamp
    // Since data is sorted, we can search forward from last position
    while (dataIndex < data.length - 1 &&
           Math.abs(data[dataIndex + 1].timestamp - timestamp) <
           Math.abs(data[dataIndex].timestamp - timestamp)) {
      dataIndex++;
    }
    result.push(data[dataIndex]);
  }

  return result;
}

/**
 * Custom hook for downsampling chart data based on container width.
 *
 * @param {Array} data - Full dataset to downsample
 * @param {Object} options - Configuration options
 * @param {number} options.maxPointsPerPixel - Points per pixel (default: 2)
 * @param {number} options.minPoints - Minimum points to keep (default: 500)
 * @param {number} options.maxPoints - Maximum points to allow (default: 3000)
 * @param {string} options.valueKey - Key for Y value in data objects (default: 'close')
 * @returns {Object} { downsampledData, containerRef, containerWidth, isDownsampled, originalCount, displayedCount }
 */
export function useDownsampledData(data, options = {}) {
  const {
    maxPointsPerPixel = DEFAULT_CONFIG.maxPointsPerPixel,
    minPoints = DEFAULT_CONFIG.minPoints,
    maxPoints = DEFAULT_CONFIG.maxPoints,
    valueKey = 'close',
  } = options;

  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(DEFAULT_CONFIG.defaultWidth);

  // Track container width using ResizeObserver
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        const width = entry.contentRect.width;
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    });

    observer.observe(containerRef.current);

    // Set initial width
    const initialWidth = containerRef.current.getBoundingClientRect().width;
    if (initialWidth > 0) {
      setContainerWidth(initialWidth);
    }

    return () => observer.disconnect();
  }, []);

  // Calculate target points based on container width
  const targetPoints = useMemo(() => {
    return Math.max(
      minPoints,
      Math.min(Math.floor(containerWidth * maxPointsPerPixel), maxPoints)
    );
  }, [containerWidth, minPoints, maxPoints, maxPointsPerPixel]);

  // Downsample data when it exceeds target
  const downsampledData = useMemo(() => {
    if (!data || data.length === 0) return data;
    if (data.length <= targetPoints) return data;

    return downsampleData(data, targetPoints, valueKey);
  }, [data, targetPoints, valueKey]);

  // Calculate stats for debugging/display
  const originalCount = data?.length || 0;
  const displayedCount = downsampledData?.length || 0;
  const isDownsampled = displayedCount < originalCount;

  return {
    downsampledData,
    containerRef,
    containerWidth,
    isDownsampled,
    originalCount,
    displayedCount,
    targetPoints,
  };
}

/**
 * Hook for downsampling data within a visible range (for zoomed charts).
 * Only downsamples the visible portion, not the full dataset.
 *
 * @param {Array} data - Full dataset
 * @param {Object} zoomRange - { startIndex, endIndex } defining visible range
 * @param {number} containerWidth - Width of chart container in pixels
 * @param {Object} options - Same as useDownsampledData options
 * @returns {Object} { visibleData, isDownsampled, stats }
 */
export function useZoomedDownsampledData(data, zoomRange, containerWidth, options = {}) {
  const {
    maxPointsPerPixel = DEFAULT_CONFIG.maxPointsPerPixel,
    minPoints = DEFAULT_CONFIG.minPoints,
    maxPoints = DEFAULT_CONFIG.maxPoints,
    valueKey = 'close',
  } = options;

  return useMemo(() => {
    if (!data || data.length === 0) {
      return { visibleData: data, isDownsampled: false, stats: null };
    }

    // Extract visible range
    const startIdx = zoomRange?.startIndex ?? 0;
    const endIdx = zoomRange?.endIndex ?? data.length - 1;
    const visibleData = data.slice(startIdx, endIdx + 1);

    // Calculate target points for this view
    const targetPoints = Math.max(
      minPoints,
      Math.min(Math.floor(containerWidth * maxPointsPerPixel), maxPoints)
    );

    // If visible data is small enough, use as-is
    if (visibleData.length <= targetPoints) {
      return {
        visibleData,
        isDownsampled: false,
        stats: {
          originalCount: visibleData.length,
          displayedCount: visibleData.length,
          targetPoints,
        },
      };
    }

    // Downsample the visible portion
    const downsampled = downsampleData(visibleData, targetPoints, valueKey);

    return {
      visibleData: downsampled,
      isDownsampled: true,
      stats: {
        originalCount: visibleData.length,
        displayedCount: downsampled.length,
        targetPoints,
      },
    };
  }, [data, zoomRange, containerWidth, maxPointsPerPixel, minPoints, maxPoints, valueKey]);
}

/**
 * Aggregate N consecutive OHLC candles into 1, preserving proper OHLC semantics.
 *
 * WHY THIS EXISTS:
 * - LTTB downsampling picks representative *points* from a single value, which
 *   doesn't preserve OHLC semantics (open of first, high of all, low of all,
 *   close of last).
 * - This function merges groups of N candles into 1 aggregated candle for LOD
 *   rendering when the chart is zoomed out.
 *
 * @param {Array} candles - Array of candle objects with open, high, low, close, timestamp
 * @param {number} factor - Number of candles to merge into 1 (e.g., 5 means 5→1)
 * @param {string} prefix - OHLC field prefix (e.g., 'ohlc_MNQ_' → fields are 'ohlc_MNQ_open', etc.)
 * @returns {Array} Aggregated candle array (shorter by ~factor)
 */
export function aggregateOhlcCandles(candles, factor, prefix = '') {
  if (!candles || candles.length === 0) return candles;
  if (!factor || factor <= 1) return candles;

  const openKey = `${prefix}open`;
  const highKey = `${prefix}high`;
  const lowKey = `${prefix}low`;
  const closeKey = `${prefix}close`;

  const result = [];
  for (let i = 0; i < candles.length; i += factor) {
    const group = candles.slice(i, Math.min(i + factor, candles.length));
    const groupLen = group.length;

    // Find first candle with valid open, last candle with valid close
    const firstOpen = group[0][openKey];
    const lastClose = group[groupLen - 1][closeKey];

    // Compute high = max of all highs, low = min of all lows
    let high = -Infinity;
    let low = Infinity;
    for (let j = 0; j < groupLen; j++) {
      const h = group[j][highKey];
      const l = group[j][lowKey];
      if (h !== undefined && h > high) high = h;
      if (l !== undefined && l < low) low = l;
    }

    // Use middle candle's timestamp for positioning
    const midIdx = Math.floor(groupLen / 2);
    const timestamp = group[midIdx].timestamp;

    result.push({
      ...group[midIdx], // Spread middle candle for any extra fields (e.g., other indicators)
      [openKey]: firstOpen,
      [highKey]: high,
      [lowKey]: low,
      [closeKey]: lastClose,
      timestamp,
      _startTimestamp: group[0].timestamp,
      _endTimestamp: group[groupLen - 1].timestamp,
      _candleCount: groupLen,
    });
  }

  return result;
}

export default useDownsampledData;
